import { COMMUNITY_INCLUDED } from "./lib/communityFeature";
import { ReviewerClient } from "./routes/ReviewerClient";
import { ReviewerReview } from "./routes/ReviewerReview";
import { ReviewerWeek } from "./routes/ReviewerWeek";
import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { SignIn } from "./routes/SignIn";
import { CoachPage } from "./routes/CoachPage";
import { PendingSendToast } from "./components/PendingSendToast";
import { WorkoutReview } from "./routes/WorkoutReview";
import { SessionOverviewWindow } from "./components/SessionOverviewWindow";
import { Dashboard } from "./routes/Dashboard";
import { Roster } from "./routes/Roster";
import { Client } from "./routes/Client";
import { Workouts } from "./routes/Workouts";
import { Programs } from "./routes/Programs";
import { Equipment } from "./routes/Equipment";
import { ProgramBoard } from "./routes/ProgramBoard";
import { ProgramNew } from "./routes/ProgramNew";
import { Exercises } from "./routes/Exercises";
import { ExerciseEditor } from "./routes/ExerciseEditor";
import { Requests } from "./routes/Requests";
import { Identity } from "./routes/Identity";
import { YourData } from "./routes/YourData";
import { WhatChanged } from "./routes/WhatChanged";
import { RemindersPage } from "./routes/Reminders";
import { WorkoutBuilder } from "./routes/WorkoutBuilder";
import { Inbox } from "./routes/Inbox";
import { CommandPalette } from "./components/CommandPalette";
import { Shell } from "./components/Shell";
import { backdropRoute, href, parseRoute, type Route } from "./lib/route";
import Join from "./routes/Join";
import { WorkoutOverviewWindow } from "./components/WorkoutOverviewWindow";
import { ensureCoachAndCheckAccess, type WebAccess } from "./lib/signUp";
import { NotACoach } from "./routes/NotACoach";
import { ExampleBanner } from "./components/ExampleBanner";
import { AUTO_APPLY_ON_OPEN, autoApplyOnOpen } from "./lib/autoApplyOnOpen";
import { exampleActive } from "./lib/example/mode";
import { calendarToday, dayKeyOf } from "./lib/days";

/* The root: who is signed in, and where in the app they are.
 *
 * ⚠️ THE ROUTER ARRIVED WITH THE SCREEN THAT NEEDED IT, WHICH IS WHY IT IS HERE NOW AND WAS NOT
 * BEFORE. Signed-out/signed-in is a CONDITION, not a route, so one screen never earned one. The
 * client detail page is the first thing you can be on while signed in that is not the roster,
 * and it is the same moment the roster's name column got somewhere to link to.
 *
 * The grammar and the hash-vs-history reasoning live in `lib/route.ts`; the three-column
 * navigation shell is `components/Shell.tsx`. What lives HERE is keeping the route honest
 * against the session.
 *
 * ⚠️ AND A CORRECTION IN PLACE: THIS HEADER USED TO CLAIM THAT MOVING FOCUS ON NAVIGATION WAS
 * DONE HERE. It never was — each screen moves focus to its own heading on arrival
 * (`Client.tsx`, `Workouts.tsx`, `WorkoutBuilder.tsx`, `Inbox.tsx`), which is the right home
 * for it because only the screen knows when its own content has settled. The claim was true of
 * the DESIGN and false of this file, which is the shape `CLAUDE.md` records a dozen times: a
 * sentence that lapses without anything turning red, believed because it was written down.
 */

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  /* 🔴 WHO THIS ACCOUNT IS, not merely whether somebody is signed in. Until 2026-09-18 this file
     gated on the SESSION alone, so an athlete signing in with a perfectly good password was handed
     the coach console. `unknown` is its own state and renders the loading line, never either
     answer: a failed read must not lock a coach out, and must not let anybody else in. */
  const [access, setAccess] = useState<WebAccess>("unknown");
  const [route, setRoute] = useState<Route>(() =>
    parseRoute(window.location.hash, window.location.pathname));
  /* ⚠️ THE COST THE WINDOW INTRODUCED, PAID HERE. As a full page the builder's backdrop did not
     exist, so closing it MOUNTED the library and fetched it. As a window the library never
     unmounts — which is the whole point, it is what stops the page flashing and what keeps the
     coach's scroll position — so a workout saved inside the window would be missing from the
     list behind it, and a coach who created one and closed would read that as the save failing.
     A counter is the smallest honest fix: the builder says a save landed, the library refetches. */
  const [librarySeq, setLibrarySeq] = useState(0);

  // `hashchange` fires for a link, for the back button and for a pasted URL alike, so there is
  // one place the route is read and no component ever pushes state by hand.
  useEffect(() => {
    const onHash = () =>
      setRoute(parseRoute(window.location.hash, window.location.pathname));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  /* 🔴 F15 STEP 3.7 — A WEB-ONLY COACH'S CLIENTS GET NEXT WEEK WRITTEN (`lib/autoApplyOnOpen.ts`): once per page load, as
     soon as this account is known to be a coach, the server writes every DUE day of every client whose automatic switch is
     ON — what the phone's runner does on its dashboard. Never in the example workspace (it writes nothing there). */
  const coachId = access === "coach" ? session?.user.id ?? null : null;
  useEffect(() => {
    if (AUTO_APPLY_ON_OPEN && coachId !== null && !exampleActive()) {
      void autoApplyOnOpen(coachId, dayKeyOf(calendarToday()),
        async (id) => {
          const { data, error } = await supabase.from("coach_clients").select("client_id, status").eq("coach_id", id);
          if (error) throw new Error(error.message);
          return (data ?? []).map((r: { client_id: string | null; status: string }) => ({ clientId: r.client_id, status: r.status }));
        },
        async (body) => {
          const { data: out, error } = await supabase.functions.invoke("apply-next-week", { body });
          if (error) return { status: 0, message: error.message };
          return { status: 200, message: (out as { message?: string } | null)?.message ?? null };
        });
    }
  }, [coachId]);

  useEffect(() => {
    // The session may already exist in storage from a previous visit, so it is read once…
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
      // The restored-from-storage path needs it too: a Google sign-in that happened on a previous
      // visit never passes through the listener below again.
      if (data.session !== null) void ensureCoachAndCheckAccess().then(setAccess);
    });
    // …and then followed. Sign-in, sign-out, token refresh and another tab signing out all
    // arrive here, which is why nothing in `SignIn` redirects by hand.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      // 🔴 A SIGNED-IN USER WITH NO `profiles` ROW IS NOT AN ACCOUNT THIS APP CAN USE — `account_role`
      // lives on that row. Google sign-in has been producing exactly that state on this page since
      // the button shipped: an `auth.users` record and nothing else, which renders as an empty
      // roster rather than as anything anybody could act on.
      //
      // ⚠️ IT ONLY EVER CREATES (see `ensureCoachProfile`), so a coach who signed up on the phone
      // keeps every field of the row they already have — including a role this would otherwise
      // stamp over. That check is what makes it safe to run after EVERY sign-in rather than only
      // after the one that came from the form.
      if (next === null) setAccess("unknown");
      else void ensureCoachAndCheckAccess().then(setAccess);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  return (
    <>
      {/* The first focusable element on the page, every page. A keyboard user should not have
          to walk the whole header to reach the content. */}
      <a className="ht-skip-link" href="#main">
        Skip to content
      </a>
      {/* The example workspace's label, on every page while it is on (`lib/example/mode.ts`). */}
      <ExampleBanner />

      {/* 🔴 NO APPEARANCE CONTROL WHILE SIGNED OUT (W81, Fable's ruling 2026-09-26). The switch is rendered exactly ONCE,
          by `Shell`, above the coach's card. The sign-in page follows the coach's STORED choice all the same — it is
          applied before the first paint by `applyStoredTheme` (main.tsx) — and with nothing stored, the system's. */}

      {/* `ready` distinguishes "not signed in" from "we have not looked yet". Rendering the
          login form during that gap flashes it at somebody who is already signed in. */}
      {!ready ? (
        <main id="main" className="app-loading">
          <p className="ht-callout" aria-live="polite">
            Loading…
          </p>
        </main>
      ) : route.name === "join" ? (
        /* 🔴 THE ONE ROUTE THAT IS CONSULTED WHILE SIGNED OUT, AND THE EXCEPTION IS STATED HERE
           BECAUSE THE RULE IS STATED HERE. The comment below is right that a deep link into a
           client page must not be honoured before we know who is asking. An INVITATION is the
           opposite case: the person opening it **has no account yet** — that is what being
           invited means — so sending them to a sign-in form with no explanation would hide the
           very thing they were sent. The page shows who invited them, then asks them to sign in,
           and the token stays in the address bar so they come back to it. Nothing is CLAIMED
           while signed out: `claim_coach_invitation` is granted to `authenticated` alone. */
        <Join token={route.token} />
      ) : session === null ? (
        // ⚠️ THE ROUTE IS NOT CONSULTED WHILE SIGNED OUT, DELIBERATELY. A deep link to a client
        // page from somebody not signed in must show the sign-in screen — and it keeps the
        // fragment, so signing in lands them where they were going rather than at the roster.
        <SignIn />
      ) : access === "unknown" ? (
        /* Signed in, but we have not yet been told which kind of account this is. Rendering the
           console here would flash the coach's screens at an athlete for as long as one round
           trip takes, which is exactly what this gate exists to prevent. */
        <main id="main" className="app-loading">
          <p className="ht-callout" aria-live="polite">
            Loading…
          </p>
        </main>
      ) : access === "notACoach" ? (
        <NotACoach />
      ) : (
        // Signed in, so the product's frame is up. Every screen below renders columns 2 and 3
        // through `Workspace`; the shell renders column 1 once, here, rather than five times.
        <Shell
          route={route}
          // The coach's card at the foot opens the account; sign out lives there, never beside the sections.
          session={session}
        >
          {/* 🔴 A WINDOW IS A ROUTE DRAWN OVER ANOTHER ROUTE'S SCREEN, AND `backdropRoute` IS THE
              ONE PLACE THAT RELATIONSHIP IS DECLARED. The screen underneath is chosen by the
              BACKDROP's route, so `#/workouts/<id>` renders the library and puts the builder over
              it — the address is unchanged and a shared workout link still opens the right thing.

              ⚠️ AND THE BACKDROP SCREEN DOES NOT REMOUNT WHEN THE WINDOW OPENS OR CLOSES, WHICH
              IS DELIBERATE AND IS THE HALF THAT MAKES IT FEEL LIKE A WINDOW. `screenFor` returns
              the same element type in the same position for `#/workouts` and `#/workouts/<id>`,
              so React keeps it mounted: no refetch, no flash, no scroll lost, and no second
              `heading.focus()` fighting the dialog for the caret. `librarySeq` above is what
              keeps a screen that cannot remount from going stale. */}
          {screenFor(backdropRoute(route) ?? route, librarySeq, backdropRoute(route) === null,
                     session.user.id, () => { setLibrarySeq((n) => n + 1); },
                     route.name === "reviewNextWeek" ? route.workoutId : null,
                     route.name === "clientWorkoutNew" || route.name === "clientWorkout" ? route : null)}
          {/* ⌘K anywhere signed in — docs/WEB-DESIGN-DIRECTION.md §2. */}
          <CommandPalette coachId={session.user.id} />
          {/* W82 step 4 — a review sent and then left by reflex keeps its Undo on whichever page comes next. */}
          <PendingSendToast />

          {/* The window itself, rendered BESIDE the screen rather than instead of it. It is a
              modal `<dialog>`, so everything above is inert while it is open — the browser's
              own guarantee rather than one this app tries to reproduce with `aria-hidden`. */}
          {route.name === "workoutNew" && (
            <WorkoutBuilder workoutId={null} route={route}
                            onSaved={() => setLibrarySeq((n) => n + 1)} />
          )}
          {route.name === "workout" && (
            <WorkoutBuilder workoutId={route.workoutId} route={route}
                            onSaved={() => setLibrarySeq((n) => n + 1)} />
          )}
          {/* W17d's overview. ⚠️ IT IS A SIBLING OF THE BUILDER, NEVER NESTED IN IT, and only one
              of the two is ever mounted because the two `route.name` tests are exclusive. Edit is
              a navigation from one address to the other, and since `backdropRoute` names
              `workouts` for BOTH, the screen behind stays mounted and the coach sees the window's
              contents change rather than a close and a re-open. */}
          {route.name === "workoutOverview" && (
            <WorkoutOverviewWindow workoutId={route.workoutId}
                                   onRemoved={() => { setLibrarySeq((n) => n + 1); }} />
          )}
          {/* W93: a finished workout's written-and-done, over the Dashboard queue row that opened it. */}
          {route.name === "sessionOverview" && <SessionOverviewWindow key={route.sessionId} sessionId={route.sessionId} />}
          {route.name === "exerciseNew" && (
            <ExerciseEditor exerciseId={null} route={route}
                            onSaved={() => setLibrarySeq((n) => n + 1)} />
          )}
          {route.name === "exercise" && (
            <ExerciseEditor exerciseId={route.exerciseId} route={route}
                            onSaved={() => setLibrarySeq((n) => n + 1)} />
          )}
        </Shell>
      )}
    </>
  );
}

/**
 * The SCREEN a route names — never a window.
 *
 * ⚠️ THE BUILDER IS ABSENT FROM THIS SWITCH ON PURPOSE, AND THAT ABSENCE IS THE STRUCTURE. A
 * window is not a screen: it has no `<main id="main">`, it does not own columns 2 and 3, and it
 * is drawn OVER whatever `backdropRoute` names. Listing it here as well would give the page two
 * `main` landmarks and two elements carrying `id="main"`, which is where the skip link lands.
 */
function screenFor(
  route: Route, librarySeq: number, foreground: boolean, userId: string,
  onLibraryChanged: () => void,
  /** 2026-10-07 — the review's `Edit next week` window (`reviewNextWeek`): the REVIEW draws it, beside its page, because the
   *  builder opens holding the review's drafted cells and its save empties them — state only the review has. */
  reviewNextWeek: string | null,
  /** William's item 4 — the builder over a client's week: the CLIENT page draws it (its name, its days, its re-read). */
  clientBuilder: Extract<Route, { name: "clientWorkoutNew" | "clientWorkout" }> | null,
) {
  return (
    <>
      {route.name === "dashboard" ? (
        <Dashboard coachId={userId} />
      ) : route.name === "client" ? (
        <Client clientId={route.clientId} coachId={userId} builder={clientBuilder ?? undefined} />
      ) : route.name === "workouts" ? (
        <Workouts reloadKey={librarySeq} foreground={foreground} />
      ) : route.name === "exercises" ? (
        <Exercises reloadKey={librarySeq} foreground={foreground} />
      ) : route.name === "programs" ? (
        <Programs reloadKey={librarySeq} foreground={foreground} />
      ) : route.name === "equipment" ? (
        <Equipment foreground={foreground} />
      ) : route.name === "programNew" ? (
        <ProgramNew coachId={userId} foreground={foreground} onSaved={onLibraryChanged} />
      ) : route.name === "program" ? (
        <ProgramBoard programId={route.programId} foreground={foreground} />
      ) : route.name === "requests" ? (
        <Requests foreground={foreground} />
      ) : route.name === "identity" && COMMUNITY_INCLUDED ? (
        <Identity foreground={foreground} />
      ) : route.name === "identity" ? (
        /* W120: the public name is out with the Community — an old link lands on the Dashboard, never a blank page. */
        <DashboardRedirect />
      ) : route.name === "data" ? (
        <YourData foreground={foreground} />
      ) : route.name === "changes" ? (
        <WhatChanged foreground={foreground} />
      ) : route.name === "reminders" ? (
        <RemindersPage coachId={userId} foreground={foreground} />
      ) : route.name === "inbox" || route.name === "thread" ? (
        /* ⚠️ ONE ELEMENT FOR BOTH ADDRESSES, ON PURPOSE. Returning the same component type in the
           same position for `#/inbox` and `#/inbox/<id>` is what keeps React from REMOUNTING it
           when the coach picks a thread — so the thread list is fetched once rather than on every
           selection, and the column-2 list does not flash. It is the same reasoning the library
           needed when the builder became a window, arrived at from the other direction. */
        <Inbox coachId={userId} selectedClientId={route.name === "thread" ? route.clientId : null} />
      ) : route.name === "page" ? (
        <CoachPage coachId={userId} />
      ) : route.name === "review" ? (
        /* KEYED by the workout: Skip and ← / → move to another review — a fresh page, never another workout's drafts. A send
           in its undo window lives in `pendingSend`, not here, so leaving keeps its Undo (the app-level toast). */
        <WorkoutReview key={route.sessionId} sessionId={route.sessionId} coachId={userId} nextWeekWorkoutId={reviewNextWeek} />
      ) : route.name === "reviewing" ? (
        /* The reviewer seat (step 2, 2026-10-10): a client who invited this account to REVIEW — never a client of his. */
        <ReviewerClient clientId={route.clientId} />
      ) : route.name === "reviewingDay" ? (
        <ReviewerReview key={route.sessionId} clientId={route.clientId} sessionId={route.sessionId} reviewerId={userId} />
      ) : route.name === "reviewingWeek" ? (
        <ReviewerWeek key={`${route.clientId}:${route.monday}`} clientId={route.clientId} monday={route.monday} />
      ) : route.name === "unknown" ? (
        <main id="main" className="app-loading">
          {/* An address this app does not have says so, rather than silently becoming the
              roster. A fallback here would make a stale or mistyped link land somewhere
              plausible with nothing to notice — CLAUDE.md's twentieth recorded instance.
              The shell stays up around it, so the way out is the nav rather than this one
              link — but the link stays, because a keyboard user is standing here. */}
          <p className="ht-callout">That page does not exist.</p>
          <p>
            <a className="ht-link" href={href({ name: "roster" })}>
              Go to your clients
            </a>
          </p>
        </main>
      ) : (
        <Roster coachId={userId} />
      )}
    </>
  );
}

/** W120: a route whose page left with the Community sends the coach to the Dashboard (replace — Back does not return). */
function DashboardRedirect() {
  useEffect(() => { window.location.replace(href({ name: "dashboard" })); }, []);
  return null;
}
